import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './DigipinFinder.css';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode'; // Added qrcode library

// Add Material Icons CSS for better icon support
const materialIconsCSS = document.createElement('link');
materialIconsCSS.rel = 'stylesheet';
materialIconsCSS.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
document.head.appendChild(materialIconsCSS);

// Fix for default markers in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DigipinFinder = () => {
  const [map, setMap] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentLocation, setCurrentLocation] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [digipin, setDigipin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [locationSource, setLocationSource] = useState('');
  const [areaLevel, setAreaLevel] = useState(8);
  const [areaBounds, setAreaBounds] = useState(null);
  const [areaCenter, setAreaCenter] = useState(null);
  const [areaLoading, setAreaLoading] = useState(false);
     const [areaRectangle, setAreaRectangle] = useState(null);
   const [mapLayers, setMapLayers] = useState(null);
  const [locationDetectionComplete, setLocationDetectionComplete] = useState(false);
  const [userLocationReady, setUserLocationReady] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  
  // Mobile UI states
  const [panelMode, setPanelMode] = useState('digipin'); // 'digipin' | 'search'
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  
  const mapRef = useRef(null);
  const markerRef = useRef(null);

  // API base URL - use the same pattern as other components
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
  console.log('🔗 API Base URL:', API_BASE_URL);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current) return;

    // Wait for the next tick to ensure DOM is ready
    const timer = setTimeout(() => {
      try {
        // Create map without setting initial view - we'll set it after location detection
        const newMap = L.map(mapRef.current);

                           // Define multiple map tile layers using free alternatives
          const mapLayers = {
            'Street Map': {
              type: 'single',
              base: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '© OpenStreetMap contributors',
                maxZoom: 19
              })
            },
            'Satellite': {
              type: 'single',
              base: L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                attribution: '© Esri, Maxar, Earthstar Geographics',
                maxZoom: 19
              })
            }
          };

                   // Store map layers for custom control immediately
          setMapLayers(mapLayers);

          // Add default layer (Street Map)
          if (mapLayers['Street Map'].base) {
            mapLayers['Street Map'].base.addTo(newMap);
          }
          
          // Set current layer state
          setCurrentLayer(mapLayers['Street Map']);
          setCurrentLayerName('Street Map');

          // Disable default zoom controls and add custom ones at bottom-right
          newMap.zoomControl.remove();
          L.control.zoom({
            position: 'bottomright'
    }).addTo(newMap);

    console.log('🗺️ About to set map state...');
    setMap(newMap);
    setMapReady(true);
    console.log('🗺️ Map state setters called, newMap:', !!newMap);
    
    // Verify the map object is valid
    if (newMap && newMap.getContainer) {
      console.log('🗺️ Map object validation passed');
    } else {
      console.error('🗺️ Map object validation failed');
    }
    
    console.log('🗺️ Map initialized successfully');
      } catch (error) {
        console.error('❌ Map initialization failed:', error);
      }
    }, 100);

    // Cleanup
    return () => {
      clearTimeout(timer);
      if (map) {
        try {
          // Remove area rectangle if exists
          if (areaRectangle) {
            map.removeLayer(areaRectangle);
          }
          map.remove();
        } catch (error) {
          console.warn('Map cleanup warning:', error);
        }
      }
    };
  }, []);

  // Detect user location on component mount
  useEffect(() => {
    // Wait for map to be initialized before detecting location
    if (map && mapReady) {
      // Always detect user location - needed for directions icon to work
      console.log('📍 Map ready, detecting user location');
      
      // Simple check if map is ready - no unnecessary delays
        if (map && map.getContainer && map.getContainer().parentNode) {
          console.log('📍 Map confirmed ready, requesting location permission');
          requestLocationPermission();
        } else {
        console.log('📍 Map not fully ready yet, waiting briefly');
        // Single short delay if needed
          setTimeout(() => {
            if (map && map.getContainer && map.getContainer().parentNode) {
              console.log('📍 Map now ready, requesting location permission');
              requestLocationPermission();
          } else {
            console.log('📍 Map still not ready, will retry location detection');
            // Don't set a fallback location - wait for proper location detection
          }
        }, 500);
      }
    }
  }, [map, mapReady]); // Removed currentLocation dependency

  // Handle URL parameters for Digipin sharing - wait for user location
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const digipinFromUrl = urlParams.get('digipin');
    
    console.log('🔍 Checking URL parameters:', { 
      search: window.location.search, 
      digipin: digipinFromUrl,
      mapReady,
      hasMap: !!map,
      userLocationReady
    });
    
    if (digipinFromUrl) {
      console.log('🔍 Digipin found in URL:', digipinFromUrl);
      setDigipin(digipinFromUrl);
      setSearchQuery(digipinFromUrl);
      
      // Wait for both map AND user location to be ready
      if (map && mapReady && userLocationReady) {
        console.log('🗺️ User location ready, now searching for Digipin from URL');
        searchDigipinFromUrl(digipinFromUrl);
      } else {
        console.log('⏳ Waiting for user location detection before URL search');
      }
    } else {
      console.log('ℹ️ No Digipin found in URL parameters');
    }
  }, [map, mapReady, userLocationReady]); // Add userLocationReady dependency

  // Add pending markers when map becomes ready - but not for URL searches
  useEffect(() => {
    // Check if URL Digipin exists
    const urlParams = new URLSearchParams(window.location.search);
    const digipinFromUrl = urlParams.get('digipin');
    
    if (map && mapReady && currentLocation && !markerRef.current && !digipinFromUrl) {
      console.log('📍 Map now ready, adding pending marker for current location');
      // Small delay to ensure map is fully settled
      setTimeout(() => {
        if (map && mapReady && currentLocation) {
          addMarker(currentLocation.lat, currentLocation.lng, 'Your Location');
        }
      }, 500);
    }
  }, [map, mapReady, currentLocation]);

  // Bind map click event when map is ready
  useEffect(() => {
    if (map && mapReady) {
      console.log('🗺️ Binding map click event - map is ready');
      
      // Remove any existing click events first
      map.off('click');
      
      // Add click event to map
      map.on('click', (e) => {
        console.log('🗺️ Map click event triggered');
        handleMapClick(e);
      });
      
      console.log('🗺️ Map click event bound successfully');
    }
  }, [map, mapReady]);

  // Debug state changes
  useEffect(() => {
    console.log('🗺️ State changed:', { 
      map: !!map, 
      mapReady, 
      currentLocation: !!currentLocation,
      mapContainer: map ? !!map.getContainer() : false
    });
  }, [map, mapReady, currentLocation]);

  // Keyboard detection for mobile
  useEffect(() => {
    const handleResize = () => {
      // Simple detection: if viewport height decreases significantly, keyboard is likely open
      const viewportHeight = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const screenHeight = window.screen.height;
      const keyboardThreshold = screenHeight * 0.75; // If viewport is less than 75% of screen, assume keyboard is open
      
      setIsKeyboardOpen(viewportHeight < keyboardThreshold);
    };

    // Listen for viewport changes (mobile keyboard)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      return () => window.visualViewport.removeEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Responsive panel state - collapse on mobile, expand on desktop
  useEffect(() => {
    const handleResponsiveResize = () => {
      const isMobile = window.innerWidth <= 768;
      setIsCollapsed(isMobile);
      
      // Update logo size based on screen width
      setUseSmallLogo(window.innerWidth <= 480);
    };

    window.addEventListener('resize', handleResponsiveResize);
    return () => window.removeEventListener('resize', handleResponsiveResize);
  }, []);

  const requestLocationPermission = () => {
    console.log('🔍 Requesting location permission...');
    
    if (!navigator.geolocation) {
      console.log('❌ Geolocation not supported');
      setError('Geolocation is not supported by this browser. Please use a different browser or manually search for your location.');
      return;
    }

    // Debug: Check current permission state
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        console.log('🔍 Current permission state:', result.state);
        console.log('🔍 Permission can be changed:', result.state !== 'denied');
        
        // If permission is already granted, use it directly
        if (result.state === 'granted') {
          console.log('✅ Permission already granted, getting location directly');
          getCurrentLocationDirectly();
          return;
        }
        
        // If permission is denied, skip GPS and go to IP
        if (result.state === 'denied') {
          console.log('❌ Permission denied, using IP location');
          detectUserLocationWithIPFallback();
          return;
        }
        
        // If permission is prompt, show our custom prompt
        console.log('❓ Permission state is prompt, showing custom prompt');
        setShowLocationPrompt(true);
      }).catch((error) => {
        console.log('🔍 Error checking permissions:', error);
        // Fallback to showing prompt
        setShowLocationPrompt(true);
      });
    } else {
      // No permissions API, show prompt
      console.log('❓ No permissions API, showing custom prompt');
      setShowLocationPrompt(true);
    }
  };

  const getCurrentLocationDirectly = () => {
    console.log('🔍 Getting current location directly (permission already granted)');
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('✅ GPS location obtained directly:', position.coords);
        console.log('✅ GPS accuracy:', position.coords.accuracy, 'meters');
        console.log('✅ GPS coordinates:', position.coords.latitude, position.coords.longitude);
        processGPSLocation(position.coords.latitude, position.coords.longitude);
      },
      (error) => {
        console.log('❌ GPS location failed:', error.message, error.code);
        console.log('❌ Error details:', {
          code: error.code,
          message: error.message,
          PERMISSION_DENIED: error.PERMISSION_DENIED,
          POSITION_UNAVAILABLE: error.POSITION_UNAVAILABLE,
          TIMEOUT: error.TIMEOUT
        });
        console.log('🔄 Falling back to IP location');
        detectUserLocationWithIPFallback();
      },
      {
        enableHighAccuracy: true,
        timeout: 20000, // Increased timeout
        maximumAge: 0
      }
    );
  };

  const handleLocationPermissionGranted = () => {
    console.log('✅ User clicked Allow Location - triggering browser permission popup');
    setShowLocationPrompt(false);
    
    // Check if we're on HTTPS (required for geolocation in most browsers)
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      console.log('❌ Geolocation requires HTTPS (except localhost)');
      setError('Geolocation requires HTTPS. Using IP-based location instead.');
      detectUserLocationWithIPFallback();
      return;
    }
    
    // Force the browser to show the permission popup by calling getCurrentPosition
    if (navigator.geolocation) {
      console.log('🔍 Calling navigator.geolocation.getCurrentPosition to trigger browser popup');
      
      // First, try to clear any cached permissions by checking current state
      if (navigator.permissions) {
        navigator.permissions.query({ name: 'geolocation' }).then((result) => {
          console.log('🔍 Current permission state before request:', result.state);
          
          // If permission is denied, show a message
          if (result.state === 'denied') {
            console.log('❌ Permission already denied, cannot show popup');
            setError('Location access was previously denied. Please enable location access in your browser settings or use IP-based location.');
            detectUserLocationWithIPFallback();
            return;
          }
          
          // Proceed with location request
          requestLocationWithPopup();
        }).catch((error) => {
          console.log('🔍 Error checking permissions, proceeding with request:', error);
          requestLocationWithPopup();
        });
      } else {
        // No permissions API, proceed directly
        requestLocationWithPopup();
      }
      
    } else {
      console.log('❌ Geolocation not supported');
      setError('Geolocation is not supported by this browser.');
      detectUserLocationWithIPFallback();
    }
  };

  const requestLocationWithPopup = () => {
    console.log('🔍 Requesting location with getCurrentPosition');
    console.log('🔍 Geolocation options:', {
      enableHighAccuracy: true,
      timeout: 20000,
      maximumAge: 0
    });
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log('✅ GPS location obtained from browser popup:', position.coords);
        console.log('✅ GPS accuracy:', position.coords.accuracy, 'meters');
        console.log('✅ GPS timestamp:', new Date(position.timestamp));
        console.log('✅ GPS coordinates:', position.coords.latitude, position.coords.longitude);
        processGPSLocation(position.coords.latitude, position.coords.longitude);
      },
      (error) => {
        console.log('❌ GPS location failed:', error.message, error.code);
        console.log('❌ Error details:', {
          code: error.code,
          message: error.message,
          PERMISSION_DENIED: error.PERMISSION_DENIED,
          POSITION_UNAVAILABLE: error.POSITION_UNAVAILABLE,
          TIMEOUT: error.TIMEOUT
        });
        
        if (error.code === error.PERMISSION_DENIED) {
          console.log('❌ User denied permission in browser popup');
          setError('Location access denied. Using IP-based location instead.');
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          console.log('❌ Position unavailable');
          setError('Location unavailable. Using IP-based location instead.');
        } else if (error.code === error.TIMEOUT) {
          console.log('❌ Location request timed out');
          setError('Location request timed out. Using IP-based location instead.');
        } else {
          console.log('❌ Unknown error:', error);
          setError('Location request failed. Using IP-based location instead.');
        }
        
        detectUserLocationWithIPFallback();
      },
      {
        enableHighAccuracy: true,
        timeout: 20000, // 20 second timeout
        maximumAge: 0 // Don't use cached location
      }
    );
  };

  const processGPSLocation = async (latitude, longitude) => {
    if (!map || !mapReady) {
      console.log('📍 Map not ready yet, skipping GPS location processing');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('✅ Processing GPS location:', { lat: latitude, lng: longitude });
      
      // Check if location is within Digipin bounds
      const isInDigipinBounds = await checkIfLocationIsInDigipinBounds(latitude, longitude);
      
      // Set location state
      setCurrentLocation({ lat: latitude, lng: longitude });
      setLocationSource('GPS');
      
      console.log(`✅ GPS location processing complete: GPS - ${latitude}, ${longitude}`);
      console.log(`✅ Location source set to: GPS`);
      
      // Check if URL Digipin exists
      const urlParams = new URLSearchParams(window.location.search);
      const digipinFromUrl = urlParams.get('digipin');
      
      if (digipinFromUrl) {
        console.log('📍 URL Digipin exists, minimal GPS location processing complete');
        setLocationDetectionComplete(true);
        setUserLocationReady(true);
        return;
      }
      
      // Normal case: Do full user location processing
      await addMarker(latitude, longitude, 'Your Location (GPS)');
      
      // Generate Digipin and area bounds if within bounds
      if (isInDigipinBounds) {
        await generateDigipinFromCoords(latitude, longitude);
      } else {
        setDigipin('N/A');
        setSelectedLocation({
          lat: latitude,
          lng: longitude,
          address: `${latitude}, ${longitude}`,
          digipin: 'N/A'
        });
        
        if (map && map.setView) {
          const zoomLevel = getZoomLevelFromDigipinLevel(currentAreaLevelRef.current);
          map.setView([latitude, longitude], zoomLevel);
        }
      }
      
      setLocationDetectionComplete(true);
      setUserLocationReady(true);
      
    } catch (error) {
      console.error('📍 Error processing GPS location:', error);
      setError('GPS location processing failed. Please try again.');
      
      if (map && map.setView) {
        map.setView([20.5937, 78.9629], 5); // Center of India
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLocationPermissionDenied = () => {
    console.log('❌ User denied location permission');
    setShowLocationPrompt(false);
    detectUserLocationWithIPFallback();
  };

  const detectUserLocationWithIPFallback = async () => {
    if (!map || !mapReady) {
      console.log('📍 Map not ready yet, skipping location detection');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Skip GPS and go directly to IP-based location
      const ipLocation = await detectLocationByIP();
      const detectedLat = ipLocation.lat;
      const detectedLng = ipLocation.lng;
      const locationSource = ipLocation.source;

      // Check if location is within Digipin bounds
      const isInDigipinBounds = await checkIfLocationIsInDigipinBounds(detectedLat, detectedLng);
      
      // Set location state
      setCurrentLocation({ lat: detectedLat, lng: detectedLng });
      setLocationSource(locationSource);
      
      console.log(`✅ IP location detection complete: ${locationSource} - ${detectedLat}, ${detectedLng}`);
      console.log(`✅ Location source set to: ${locationSource}`);
      
      // Check if URL Digipin exists
      const urlParams = new URLSearchParams(window.location.search);
      const digipinFromUrl = urlParams.get('digipin');
      
      if (digipinFromUrl) {
        console.log('📍 URL Digipin exists, minimal user location detection complete');
        setLocationDetectionComplete(true);
        setUserLocationReady(true);
        return;
      }
      
      // Normal case: Do full user location processing
      await addMarker(detectedLat, detectedLng, `Your Location (${locationSource})`);
      
      // Generate Digipin and area bounds if within bounds
      if (isInDigipinBounds) {
        await generateDigipinFromCoords(detectedLat, detectedLng);
      } else {
        setDigipin('N/A');
        setSelectedLocation({
          lat: detectedLat,
          lng: detectedLng,
          address: `${detectedLat}, ${detectedLng}`,
          digipin: 'N/A'
        });
        
        if (map && map.setView) {
          const zoomLevel = getZoomLevelFromDigipinLevel(currentAreaLevelRef.current);
          map.setView([detectedLat, detectedLng], zoomLevel);
        }
      }
      
      setLocationDetectionComplete(true);
      setUserLocationReady(true);
      
    } catch (error) {
      console.error('📍 Error in IP location detection:', error);
      setError('Location detection failed. Please search for your location manually.');
      
      if (map && map.setView) {
        map.setView([20.5937, 78.9629], 5); // Center of India
      }
    } finally {
      setLoading(false);
    }
  };

  const detectUserLocation = async (skipGPS = false) => {
    console.log('🔍 Starting location detection...');
    
    if (!map || !mapReady) {
      console.log('📍 Map not ready yet, skipping location detection');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let detectedLat, detectedLng, locationSource;
      
      // Try browser geolocation first (unless we're skipping GPS)
      if (navigator.geolocation && !skipGPS) {
        console.log('🔍 Attempting GPS location detection...');
        try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 300000 // 5 minutes
          });
        });

        const { latitude, longitude } = position.coords;
        console.log('✅ GPS location detected:', { lat: latitude, lng: longitude });
          detectedLat = latitude;
          detectedLng = longitude;
          locationSource = 'GPS';
    } catch (error) {
      console.log('❌ GPS location failed:', error.message);
      console.log('🔄 Falling back to IP-based location...');
        }
    } else {
      console.log('❌ Geolocation not supported or skipped, using IP-based location...');
    }

      // Fallback to IP-based location detection if GPS failed
      if (!detectedLat || !detectedLng) {
    try {
          const ipLocation = await detectLocationByIP();
          detectedLat = ipLocation.lat;
          detectedLng = ipLocation.lng;
          locationSource = ipLocation.source;
    } catch (error) {
      console.error('📍 IP location detection failed:', error);
          // No fallback - let the user know location detection failed
          throw new Error('Location detection failed. Please enable location access or try again.');
        }
      }

      // Now check if location is within Digipin bounds
      const isInDigipinBounds = await checkIfLocationIsInDigipinBounds(detectedLat, detectedLng);
      
      // Set location state - ALWAYS do this
      setCurrentLocation({ lat: detectedLat, lng: detectedLng });
      setLocationSource(locationSource);
      
      console.log(`✅ Location detection complete: ${locationSource} - ${detectedLat}, ${detectedLng}`);
      
      // Check if URL Digipin exists
      const urlParams = new URLSearchParams(window.location.search);
      const digipinFromUrl = urlParams.get('digipin');
      
      if (digipinFromUrl) {
        // URL case: ONLY set currentLocation, skip everything else
        console.log('📍 URL Digipin exists, minimal user location detection complete');
        setLocationDetectionComplete(true);
        setUserLocationReady(true); // Signal completion for URL search
        return; // Exit early
      }
      
      // Normal case: Do full user location processing
      await addMarker(detectedLat, detectedLng, `Your Location (${locationSource})`);
      
      // Generate Digipin and area bounds if within bounds
      if (isInDigipinBounds) {
        await generateDigipinFromCoords(detectedLat, detectedLng);
      } else {
        // Show "N/A" message
        setDigipin('N/A');
        setSelectedLocation({
          lat: detectedLat,
          lng: detectedLng,
          address: `${detectedLat}, ${detectedLng}`,
          digipin: 'N/A'
        });
        console.log('📍 Location outside Digipin bounds, showing "N/A"');
        
        // Set map view to show the user's location even if outside Digipin bounds
        if (map && map.setView) {
          const zoomLevel = getZoomLevelFromDigipinLevel(currentAreaLevelRef.current);
          map.setView([detectedLat, detectedLng], zoomLevel);
          console.log(`🗺️ Map zoomed to user location (outside Digipin bounds) at zoom level ${zoomLevel} based on DigiPin level ${currentAreaLevelRef.current}`);
        }
      }
      
      setLocationDetectionComplete(true);
      setUserLocationReady(true); // Signal completion for normal flow
      
    } catch (error) {
      console.error('📍 Error in location detection:', error);
      setError('Location detection failed. You can still search for addresses manually or click on the map to get a DigiPin.');
      
      // Fallback: Set map to a default view (India) if location detection fails
      if (map && map.setView) {
        map.setView([20.5937, 78.9629], 5); // Center of India
        console.log('🗺️ Map set to default view (India) due to location detection failure');
      }
    } finally {
    setLoading(false);
    }
  };

  const detectLocationByIP = async () => {
    console.log('🌐 Starting IP-based location detection...');
    console.log('🌐 Note: Using frontend-only IP detection to get user\'s real IP');
    
    const services = [
      // Use external services directly from frontend to get user's real IP
      'https://ipapi.co/json/',
      'https://ipinfo.io/json',
      'https://ip-api.com/json',
      'https://api.ipgeolocation.io/ipgeo?apiKey=free',
      'https://extreme-ip-lookup.com/json/'
    ];

    for (const service of services) {
      try {
        console.log(`🌐 Trying IP service: ${service}`);
        const response = await fetch(service, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          signal: AbortSignal.timeout(5000)
        });

        console.log(`🌐 Response status: ${response.status} ${response.statusText}`);

        if (response.ok) {
          const data = await response.json();
          console.log(`🌐 IP service response:`, data);
          
          let lat, lng, country;
          
          // Parse response based on service
          if (service.includes('ipapi.co')) {
            lat = data.latitude;
            lng = data.longitude;
            country = data.country_name;
          } else if (service.includes('ipinfo.io')) {
            const coords = data.loc?.split(',');
            lat = coords ? parseFloat(coords[0]) : null;
            lng = coords ? parseFloat(coords[1]) : null;
            country = data.country;
          } else if (service.includes('ip-api.com')) {
            lat = data.lat;
            lng = data.lon;
            country = data.country;
          } else if (service.includes('ipgeolocation.io')) {
            lat = data.latitude;
            lng = data.longitude;
            country = data.country_name;
          } else if (service.includes('extreme-ip-lookup.com')) {
            lat = data.lat;
            lng = data.lon;
            country = data.country;
          }

          if (lat && lng) {
             console.log(`✅ IP location detected: ${lat}, ${lng} in ${country}`);
             return {
               lat,
               lng,
               source: `IP Detection - ${country}`
             };
          } else {
            console.log(`❌ Invalid coordinates from ${service}: lat=${lat}, lng=${lng}`);
          }
        } else {
          console.log(`❌ HTTP error from ${service}: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        console.log(`❌ IP service failed: ${service}`, error.message);
        continue;
      }
    }
    
    // If all external services fail, return a default location (center of India)
    console.log('❌ All IP services failed, using default location (India)');
    return {
      lat: 20.5937,
      lng: 78.9629,
      source: 'Default Location - India'
    };
  };

  const checkIfLocationIsInDigipinBounds = async (lat, lng) => {
    try {
      // Fast, approximate bounds check for Digipin applicability (India)
      if (lat < 6.0 || lat > 37.0 || lng < 68.0 || lng > 97.0) {
        return false;
      }
      return true;
    } catch (error) {
      console.error('📍 Error checking Digipin bounds:', error);
      return false;
    }
  };

  const addMarker = async (lat, lng, title) => {
    if (!map || !mapReady) {
      console.log('📍 Map not ready yet, skipping marker addition');
      return;
    }
    
    // Additional safety check - ensure map container exists and is properly initialized
    if (!map.getContainer() || !map.getContainer().parentNode) {
      console.log('📍 Map container not properly initialized, skipping marker addition');
      return;
    }
    
    try {
      // Ensure map container is properly initialized
      if (!map.getContainer() || !map.getContainer().parentNode) {
        console.log('📍 Map container not properly initialized, skipping marker addition');
        return;
      }
      
      // Remove existing marker
      if (markerRef.current) {
        map.removeLayer(markerRef.current);
        markerRef.current = null;
      }

              // Map view will be set by the calling function if needed
        
        // Create a custom Leaflet marker icon
        const customIcon = L.divIcon({
          html: '📍',
          className: 'custom-marker-icon',
          iconSize: [32, 32],
          iconAnchor: [16, 32], // Point the bottom center of the icon
          popupAnchor: [0, -32] // Popup appears above the marker
        });
        
        // Create Leaflet marker
        const marker = L.marker([lat, lng], {
          icon: customIcon,
          title: title
        }).addTo(map);
        
        // Add popup with title
        marker.bindPopup(title);
        
        // Store reference for later removal
        markerRef.current = marker;
        
        console.log('📍 Leaflet marker added successfully at:', { lat, lng });
      
    } catch (error) {
      console.error('❌ Error adding Leaflet marker:', error);
    }
  };

  const handleMapClick = async (e) => {
    console.log('🗺️ Component handleMapClick called with state:', { map: !!map, mapReady, currentLocation: !!currentLocation });
    
    if (!map || !mapReady) {
      console.log('🗺️ Map not ready, returning early');
      return;
    }
    
    try {
      const { lat, lng } = e.latlng;
      console.log('🗺️ Map clicked:', { lat, lng });
      
      // Reset search box and clear any errors when clicking on map
      setSearchQuery('');
      setError('');
      setShowToast(false); // Clear any existing toast messages
      
      // Check if location is within Digipin bounds
      const isInDigipinBounds = await checkIfLocationIsInDigipinBounds(lat, lng);
      
      // Add marker first
      await addMarker(lat, lng, 'Selected Location');
      // Don't overwrite currentLocation - it should always represent user's detected location
      setLocationDetectionComplete(true);
      
      // Map view will be set by fetchDigipinAreaBounds if within bounds
      
      // Generate Digipin only if within bounds
      if (isInDigipinBounds) {
        await generateDigipinFromCoords(lat, lng, currentAreaLevelRef.current);
      } else {
        // Show "N/A" message
        setDigipin('N/A');
        setSelectedLocation({
          lat: lat,
          lng: lng,
          address: `${lat}, ${lng}`,
          digipin: 'N/A'
        });
        console.log('📍 Location outside Digipin bounds, showing "N/A"');
        
        // Set map view to show the clicked location even if outside Digipin bounds
        if (map && map.setView) {
          const zoomLevel = getZoomLevelFromDigipinLevel(currentAreaLevelRef.current);
          map.setView([lat, lng], zoomLevel);
          console.log(`🗺️ Map zoomed to clicked location (outside Digipin bounds) at zoom level ${zoomLevel} based on DigiPin level ${currentAreaLevelRef.current}`);
        }
      }
    } catch (error) {
      console.error('❌ Error handling map click:', error);
    }
  };

  const generateDigipinFromCoords = async (lat, lng, level = areaLevel) => {
    try {
      // Check if location is within Digipin bounds before making API call
      const isInDigipinBounds = await checkIfLocationIsInDigipinBounds(lat, lng);
      
      if (!isInDigipinBounds) {
        // Location outside Digipin bounds
        setDigipin('N/A');
        setSelectedLocation({
          lat: lat,
          lng: lng,
          address: `${lat}, ${lng}`,
          digipin: 'N/A'
        });
        console.log('📍 Location outside Digipin bounds, showing "N/A"');
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/public/generate-digipin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lat: lat,
          lng: lng,
          get_address: true
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setDigipin(data.digipin);
          setSelectedLocation({
          lat: lat,
          lng: lng,
          address: data.address || `${lat}, ${lng}`,
          digipin: data.digipin
        });
        
                 // Fetch area bounds for the new DigiPin
         await fetchDigipinAreaBounds(data.digipin, level);
      } else {
        console.error('❌ Error generating DigiPin:', response.status);
      }
    } catch (error) {
      console.error('❌ Error generating DigiPin:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError('');
    setShowToast(false); // Clear any existing toast messages

    try {
      // Check if it's a DigiPin
      if (searchQuery.includes('-')) {
        // Handle DigiPin search
        const response = await fetch(`${API_BASE_URL}/public/get-address`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            digipin: searchQuery.trim()
          }),
        });

        if (response.ok) {
          const data = await response.json();
          // Use the address from API, or fallback to coordinates if address is missing
          const address = data.address && data.address.trim() !== '' 
            ? data.address 
            : `${data.lat.toFixed(6)}, ${data.lng.toFixed(6)}`;
          
          setSelectedLocation({
            lat: data.lat,
            lng: data.lng,
            address: address,
            digipin: data.digipin
          });
          setDigipin(data.digipin);
          addMarker(data.lat, data.lng, address);
          // Don't overwrite currentLocation - it should always represent user's detected location
          setLocationDetectionComplete(true);
          
          // Update URL with the Digipin
          const url = new URL(window.location);
          url.searchParams.set('digipin', data.digipin);
          window.history.replaceState({}, '', url);
          
          // Fetch area bounds for the DigiPin - this will handle map positioning
          await fetchDigipinAreaBounds(data.digipin, currentAreaLevelRef.current);
        } else {
          setError('Invalid DigiPin or address not found');
        }
      } else {
        // Handle address search
        const response = await fetch(`${API_BASE_URL}/public/geocode-address`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          address: searchQuery.trim()
        }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Check if location is within Digipin bounds
        const isInDigipinBounds = await checkIfLocationIsInDigipinBounds(data.lat, data.lng);
          
        setSelectedLocation({
          lat: data.lat,
          lng: data.lng,
            address: data.address,
          digipin: isInDigipinBounds ? data.digipin : 'N/A'
        });
        setDigipin(isInDigipinBounds ? data.digipin : 'N/A');
        
        // Add marker first
        await addMarker(data.lat, data.lng, data.address);
        // Don't overwrite currentLocation - it should always represent user's detected location
        setLocationDetectionComplete(true);
      
        // Map view will be set by fetchDigipinAreaBounds if within bounds
        
        // Update URL with the Digipin if valid
        if (isInDigipinBounds) {
          const url = new URL(window.location);
          url.searchParams.set('digipin', data.digipin);
          window.history.replaceState({}, '', url);
          
          // Fetch area bounds only if within Digipin bounds
          await fetchDigipinAreaBounds(data.digipin, currentAreaLevelRef.current);
        } else {
          // Set map view to show the searched location even if outside Digipin bounds
          if (map && map.setView) {
            const zoomLevel = getZoomLevelFromDigipinLevel(currentAreaLevelRef.current);
            map.setView([data.lat, data.lng], zoomLevel);
            console.log(`🗺️ Map zoomed to searched location (outside Digipin bounds) at zoom level ${zoomLevel} based on DigiPin level ${currentAreaLevelRef.current}`);
          }
        }
      } else {
          showErrorMessage('Location not found. You can only search for pincode, town, city, etc. To get the DigiPin of a specific location, click (or long press on mobile) on the map.');
        }
      }
    } catch (error) {
      console.error('❌ Search error:', error);
      showErrorMessage('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Track current request to prevent overlapping requests
  const currentBoundsRequestRef = useRef(null);

  // Helper function to get appropriate zoom level based on DigiPin level
  const getZoomLevelFromDigipinLevel = (level) => {
    if (level >= 8) {
      return 16; // Higher zoom for smaller areas
    } else if (level >= 5) {
      return 12; // Medium zoom for medium areas
    } else if (level >= 3) {
      return 8; // Lower zoom for larger areas
    } else {
      return 5; // Very low zoom for very large areas
    }
  };

  const fetchDigipinAreaBounds = async (digipinStr, level = areaLevel) => {
    console.log(`🔍 Fetching DigiPin bounds for: ${digipinStr} at level: ${level}`);
    
    if (!digipinStr || !map) {
      console.log(`❌ Cannot fetch bounds: digipinStr=${!!digipinStr}, map=${!!map}`);
      return;
    }
    
    // Additional check to ensure map is fully ready
    if (!mapReady || !map.getContainer || !map.getContainer().parentNode) {
      console.log(`❌ Map not fully ready yet, waiting...`);
      // Wait a bit and try again
      setTimeout(() => {
        if (map && mapReady) {
          console.log(`🔄 Retrying fetchDigipinAreaBounds after map ready`);
          fetchDigipinAreaBounds(digipinStr, level);
        }
      }, 500);
      return;
    }
    
    // Cancel any ongoing request for the same Digipin
    if (currentBoundsRequestRef.current) {
      console.log(`🔄 Cancelling previous bounds request for: ${digipinStr}`);
      currentBoundsRequestRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    currentBoundsRequestRef.current = abortController;
    
    setAreaLoading(true);
    try {
      console.log(`📡 Making API request to: ${API_BASE_URL}/public/digipin-bounds?digipin=${digipinStr}&level=${level}`);
      const response = await fetch(`${API_BASE_URL}/public/digipin-bounds?digipin=${digipinStr}&level=${level}`, {
        signal: abortController.signal
      });
      
      console.log(`📡 API response status: ${response.status}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log(`✅ Received bounds data:`, data);
        
        // Remove existing rectangle if present
        if (areaRectangle) {
          console.log(`🗑️ Removing existing area rectangle`);
          try {
            map.removeLayer(areaRectangle);
            console.log(`✅ Existing rectangle removed from map`);
          } catch (error) {
            console.warn(`⚠️ Warning when removing existing rectangle:`, error);
          }
        }
        
        // Additional safety: Remove all rectangles from map to ensure clean state
        map.eachLayer((layer) => {
          if (layer instanceof L.Rectangle) {
            console.log(`🗑️ Removing orphaned rectangle from map`);
            map.removeLayer(layer);
          }
        });
        
                // Clear the areaRectangle state to ensure clean state
        setAreaRectangle(null);
        
        // Small delay to ensure the old rectangle is completely removed
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Create new rectangle
        const bounds = [
          [data.bounds.south, data.bounds.west],
          [data.bounds.north, data.bounds.east]
        ];
         
         console.log(`📐 Creating rectangle with bounds:`, bounds);
         
         // Calculate the size of the bounds to determine appropriate padding
         const latDiff = Math.abs(data.bounds.north - data.bounds.south);
         const lngDiff = Math.abs(data.bounds.east - data.bounds.west);
         
         console.log(`📏 Bounds dimensions: lat diff=${latDiff}, lng diff=${lngDiff}`);
         
         // For very small areas (like level 10), ensure minimum zoom level
         let minZoom = 15; // Default minimum zoom
         if (level >= 8) {
           minZoom = 16; // Higher zoom for smaller areas
         } else if (level >= 5) {
           minZoom = 12; // Medium zoom for medium areas
         } else if (level >= 3) {
           minZoom = 8; // Lower zoom for larger areas
         } else {
           minZoom = 5; // Very low zoom for very large areas
         }
         
         console.log(`🔍 Setting minZoom to: ${minZoom} for level: ${level}`);
         
         const rectangle = L.rectangle(bounds, {
           color: '#dc2626',
           weight: 2,
           fillColor: '#dc2626',
           fillOpacity: 0.2
         });
         
         console.log(`📐 Rectangle created:`, rectangle);
         
         // Add rectangle to map
         rectangle.addTo(map);
         
         console.log(`✅ Rectangle added to map. Map layers count:`, map.getLayers ? map.getLayers().length : 'N/A');
         
         // Verify the rectangle is actually on the map
         if (map.hasLayer && map.hasLayer(rectangle)) {
           console.log(`✅ Rectangle confirmed to be on map`);
         } else {
           console.warn(`⚠️ Rectangle may not be properly added to map`);
           // Try to add it again
           try {
             rectangle.addTo(map);
             console.log(`🔄 Rectangle re-added to map`);
           } catch (error) {
             console.error(`❌ Failed to re-add rectangle:`, error);
           }
         }
         
         // Additional verification
         setTimeout(() => {
           if (map.hasLayer && map.hasLayer(rectangle)) {
             console.log(`✅ Rectangle still on map after delay`);
           } else {
             console.warn(`⚠️ Rectangle disappeared from map after delay`);
           }
         }, 100);
         
         setAreaRectangle(rectangle);
         setAreaBounds(data.bounds);
         setAreaCenter(data.center);
         
         // Fit map to show the entire rectangle with appropriate padding
         const padding = level >= 8 ? [80, 80] : level >= 5 ? [60, 60] : [40, 40];
         
         console.log(`🗺️ Fitting map to bounds with padding:`, padding);
         
         // Always fit bounds to ensure smooth initial positioning
         map.fitBounds(bounds, { 
           padding: padding,
           maxZoom: 18,
           minZoom: minZoom,
           animate: true
         });
         
         console.log(`✅ Map fitted to bounds successfully`);
        
      } else {
        console.error('❌ Error fetching DigiPin bounds:', response.status);
        const errorText = await response.text();
        console.error('❌ Error response body:', errorText);
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log(`🔄 Bounds request was cancelled for: ${digipinStr}`);
        return;
      }
      console.error('❌ Error fetching DigiPin bounds:', error);
    } finally {
      // Clear the current request reference if this is the active request
      if (currentBoundsRequestRef.current === abortController) {
        currentBoundsRequestRef.current = null;
      }
      setAreaLoading(false);
      console.log(`🏁 Finished fetching DigiPin bounds`);
    }
  };

  // QR Code Generation and Sharing Functions
  const generateQRCode = async () => {
    if (!digipin || digipin === 'N/A') return;
    
    const shareUrl = `${window.location.origin}/digipin-finder?digipin=${digipin}`;
    
    try {
      // Generate real QR code using the qrcode library
      const dataUrl = await QRCode.toDataURL(shareUrl, {
        width: 200,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        },
        errorCorrectionLevel: 'M' // Medium error correction level
      });
      
      setQrCodeDataUrl(dataUrl);
      setShowQRModal(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
      alert('Failed to generate QR code. Please try again.');
    }
  };

  const shareDigipin = async () => {
    if (!digipin || digipin === 'N/A') return;
    
    const shareUrl = `${window.location.origin}/digipin-finder?digipin=${digipin}`;
    const shareText = `Check out this location: ${selectedLocation?.address || 'Unknown location'}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Check out this location!',
          text: shareText,
          url: shareUrl
        });
      } catch (error) {
        console.log('Share cancelled or failed:', error);
        // Fallback to manual sharing options
        showSharingOptions(shareUrl, shareText);
      }
    } else {
      // Fallback: show sharing options
      showSharingOptions(shareUrl, shareText);
    }
  };



  const showSharingOptions = (url, text) => {
    const options = [
      { name: 'Copy Link', action: () => copyToClipboard(url) },
      { name: 'Facebook', action: () => shareToFacebook(url, text) },
      { name: 'WhatsApp', action: () => shareToWhatsApp(url, text) },
      { name: 'LinkedIn', action: () => shareToLinkedIn(url, text) },
      { name: 'Email', action: () => shareViaEmail(url, text) },
      { name: 'SMS', action: () => shareViaSMS(url, text) }
    ];

    const optionNames = options.map(opt => opt.name).join('\n• ');
    const choice = prompt(`Choose sharing option:\n\n• ${optionNames}\n\nEnter the number (1-6):`);
    
    if (choice && choice >= 1 && choice <= 6) {
      options[choice - 1].action();
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      alert('Failed to copy to clipboard');
    }
  };

  // Mobile-specific functions
  const copyDigipin = async () => {
    if (!digipin || digipin === 'N/A') return;
    
    try {
      await navigator.clipboard.writeText(digipin);
      showToastMessage('DigiPin copied!');
    } catch (error) {
      console.error('Failed to copy DigiPin:', error);
      showToastMessage('Failed to copy DigiPin');
    }
  };

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const dismissToast = () => {
    setShowToast(false);
  };

  // Helper function to show error message - toast for collapsed view, inline for expanded view
  const showErrorMessage = (message) => {
    if (isCollapsed) {
      showToastMessage(message);
    } else {
      setError(message);
    }
  };

  const handleSearchToggle = () => {
    if (panelMode === 'digipin') {
      setPanelMode('search');
    } else {
      setPanelMode('digipin');
      setSearchQuery(''); // Clear search when returning to digipin mode
    }
  };

  const handleExpandCollapse = () => {
    const newCollapsedState = !isCollapsed;
    
    // When expanding from collapsed state, ensure it's positioned on the left
    if (newCollapsedState === false && isCollapsed === true) {
      // Reset position to left side when expanding
      setSearchPanelPosition({ x: 10, y: searchPanelPosition.y });
    }
    
    setIsCollapsed(newCollapsedState);
  };

  const handleMobileSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      handleSearch(e);
      setPanelMode('digipin'); // Return to digipin display after search
    }
  };

  const shareToFacebook = (url, text) => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const shareToWhatsApp = (url, text) => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareToLinkedIn = (url, text) => {
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(linkedinUrl, '_blank', 'width=600,height=400');
  };

  const shareViaEmail = (url, text) => {
    const subject = 'Check out this location!';
    const body = `${text}\n\n${url}`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  const shareViaSMS = (url, text) => {
    const body = `${text} ${url}`;
    const smsUrl = `sms:?body=${encodeURIComponent(body)}`;
    window.location.href = smsUrl;
  };



  // Show sharing options specifically for QR code images
  const showQRImageSharingOptions = (file, digipin, location) => {
    const options = [
      { name: 'Download QR Code', action: () => downloadQRCode(file) },
      { name: 'Copy QR Code Link', action: () => copyQRCodeLink(digipin) },
      { name: 'Share via Email', action: () => shareQRCodeViaEmail(file, digipin, location) },
      { name: 'Share via WhatsApp', action: () => shareQRCodeViaWhatsApp(file, digipin, location) }
    ];

    const optionNames = options.map(opt => opt.name).join('\n• ');
    const choice = prompt(`Choose how to share the QR Code:\n\n• ${optionNames}\n\nEnter the number (1-4):`);
    
    if (choice && choice >= 1 && choice <= 4) {
      options[choice - 1].action();
    }
  };

  const downloadQRCode = (file) => {
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    link.click();
    URL.revokeObjectURL(url);
    alert('QR Code downloaded! You can now share the image file.');
  };

  const copyQRCodeLink = (digipin) => {
    const shareUrl = `${window.location.origin}/digipin-finder?digipin=${digipin}`;
    navigator.clipboard.writeText(shareUrl);
    alert('Digipin link copied to clipboard!');
  };

  const shareQRCodeViaEmail = (file, digipin, location) => {
    const subject = 'QR Code for Digipin Location';
    const body = `Hi,\n\nHere's the QR code for the location: ${location}\n\nYou can scan this QR code to quickly access the location.\n\nBest regards`;
    
    // For email, we'll provide instructions since we can't attach files via mailto
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body + '\n\nNote: The QR code image has been downloaded to your device. Please attach it manually to your email.')}`;
    window.location.href = mailtoUrl;
    
    // Also download the file for manual attachment
    downloadQRCode(file);
  };

  const shareQRCodeViaWhatsApp = (file, digipin, location) => {
    const text = `Check out this QR code for location: ${location}\n\nScan it to quickly access the location!`;
    
    // WhatsApp Web sharing
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
    
    // Also download the file for manual sharing
    downloadQRCode(file);
  };

  // Function to search for a Digipin from URL parameters
  const searchDigipinFromUrl = async (digipinStr) => {
    console.log('🔍 Searching for Digipin from URL:', digipinStr);
    
    if (!digipinStr || !map) {
      console.log('❌ Cannot search: digipinStr or map not available');
      return;
    }
    
    try {
      // Use the correct endpoint to get address from Digipin
      const response = await fetch(`${API_BASE_URL}/public/get-address`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          digipin: digipinStr
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Digipin data received:', data);
        
        if (data.lat && data.lng) {
          // We have coordinates, add marker and show location
          // Use the address from API, or fallback to coordinates if address is missing
          const address = data.address && data.address.trim() !== '' 
            ? data.address 
            : `${data.lat.toFixed(6)}, ${data.lng.toFixed(6)}`;
          
          console.log('📍 Setting location:', { lat: data.lat, lng: data.lng, address });
          
          // Set selected location
          setSelectedLocation({
            lat: data.lat,
            lng: data.lng,
            address: address,
            digipin: digipinStr
          });
          
          // Also set the digipin state for UI consistency
          setDigipin(digipinStr);
          
          // Map will be positioned by fetchDigipinAreaBounds later
          
          // Add marker to map without triggering additional operations
          try {
            // Remove existing marker
            if (markerRef.current) {
              map.removeLayer(markerRef.current);
              markerRef.current = null;
            }
            
            // Create a custom Leaflet marker icon
            const customIcon = L.divIcon({
              html: '📍',
              className: 'custom-marker-icon',
              iconSize: [32, 32],
              iconAnchor: [16, 32],
              popupAnchor: [0, -32]
            });
            
            // Create Leaflet marker
            const marker = L.marker([data.lat, data.lng], {
              icon: customIcon,
              title: address
            }).addTo(map);
            
            // Add popup with title
            marker.bindPopup(address);
            
            // Store reference for later removal
            markerRef.current = marker;
            
            console.log('📍 Marker added for URL search at:', { lat: data.lat, lng: data.lng });
          } catch (error) {
            console.error('❌ Error adding marker for URL search:', error);
          }
          
          // Fetch area bounds for this Digipin - this will handle proper zooming
          await fetchDigipinAreaBounds(digipinStr, areaLevel);
          
          console.log('✅ Digipin from URL successfully displayed on map');
        } else {
          console.log('⚠️ No coordinates found for Digipin:', digipinStr, 'Data:', data);
          setError(`Digipin ${digipinStr} not found or invalid`);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Error fetching Digipin data:', response.status, 'Response:', errorText);
        setError(`Failed to fetch Digipin ${digipinStr} (Status: ${response.status})`);
      }
    } catch (error) {
      console.error('❌ Error searching for Digipin from URL:', error);
      setError(`Error searching for Digipin ${digipinStr}`);
    }
  };

  // Function to share QR code directly from the modal (no need to regenerate)
  const shareQRCodeFromModal = async () => {
    if (!qrCodeDataUrl || !digipin) return;
    
    const location = selectedLocation?.address || 'Unknown location';
    
    try {
      // Convert data URL to blob for sharing
      const response = await fetch(qrCodeDataUrl);
      const blob = await response.blob();
      
      // Create a file from the blob
      const file = new File([blob], `digipin-${digipin}-qr.png`, { type: 'image/png' });
      
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        // Modern browsers support file sharing
        await navigator.share({
          title: 'QR Code for Digipin',
          text: `QR Code for location: ${location}`,
          files: [file]
        });
      } else {
        // Fallback: show sharing options for the image
        showQRImageSharingOptions(file, digipin, location);
      }
    } catch (error) {
      console.error('Error sharing QR code from modal:', error);
      
      // Ultimate fallback: show sharing options
      const file = new File([], `digipin-${digipin}-qr.png`, { type: 'image/png' });
      showQRImageSharingOptions(file, digipin, location);
    }
  };

  // Debounce timer for area level changes
  const areaLevelDebounceRef = useRef(null);
  
  // Ref to store current area level for immediate access
  const currentAreaLevelRef = useRef(8);
  
  // Draggable search panel state
  const [searchPanelPosition, setSearchPanelPosition] = useState({ x: 10, y: 10 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768); // Collapsed on mobile, expanded on desktop
  
  // Map layer state
  const [currentLayer, setCurrentLayer] = useState(null);
  const [currentLayerName, setCurrentLayerName] = useState('Street Map');
  
  // Logo state for responsive behavior
  const [useSmallLogo, setUseSmallLogo] = useState(window.innerWidth <= 480);
  
  
  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (areaLevelDebounceRef.current) {
        clearTimeout(areaLevelDebounceRef.current);
      }
      if (currentBoundsRequestRef.current) {
        currentBoundsRequestRef.current.abort();
      }
    };
  }, []);
  
  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);
  
  // Mouse event handlers for dragging
  const handleDragMouseDown = (e) => {
    // Only prevent text selection when actually starting a drag
    if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'TEXTAREA' && e.target.tagName !== 'BUTTON') {
      e.preventDefault();
      e.stopPropagation();
      
      setIsDragging(true);
      const panelRect = e.currentTarget.closest('.search-panel').getBoundingClientRect();
      setDragOffset({
        x: e.clientX - panelRect.left,
        y: e.clientY - panelRect.top
      });
      
      // Temporarily disable text selection during drag
      document.body.style.userSelect = 'none';
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    e.preventDefault();
    
    const newX = e.clientX - dragOffset.x;
    const newY = e.clientY - dragOffset.y;
    
    // Keep panel within viewport bounds
    const panelWidth = isCollapsed ? 140 : 240;
    const maxX = window.innerWidth - panelWidth;
    const maxY = window.innerHeight - 200;
    
    const boundedX = Math.max(0, Math.min(newX, maxX));
    const boundedY = Math.max(0, Math.min(newY, maxY));
    
    setSearchPanelPosition({
      x: boundedX,
      y: boundedY
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    // Re-enable text selection after drag
    document.body.style.userSelect = '';
  };

  const handleAreaLevelChange = (newLevel) => {
    console.log(`🎚️ Area level changed from ${areaLevel} to ${newLevel}`);
    console.log(`🔍 Current state: digipin=${digipin}, currentLocation=`, currentLocation);
    
    // Prevent processing if the level hasn't actually changed
    if (newLevel === areaLevel) {
      console.log(`ℹ️ Area level unchanged, skipping processing`);
      return;
    }
    
    // Clear any existing debounce timer
    if (areaLevelDebounceRef.current) {
      clearTimeout(areaLevelDebounceRef.current);
    }
    
    // Set the new level immediately for UI responsiveness
    setAreaLevel(newLevel);
    currentAreaLevelRef.current = newLevel; // Update ref for immediate access
    
    // Debounce the actual API call to prevent rapid successive requests
    areaLevelDebounceRef.current = setTimeout(() => {
    // If we have a DigiPin, fetch bounds for that
    if (digipin) {
        console.log(`✅ Fetching bounds for existing DigiPin: ${digipin} at level: ${newLevel}`);
      fetchDigipinAreaBounds(digipin, newLevel);
    }
      // If we have a current location but no Digipin, we could generate a Digipin for the current location
    else if (currentLocation && currentLocation.lat && currentLocation.lng) {
        console.log(`⚠️ No Digipin but have current location - showing message`);
      // For now, just show a message that they need to search first
        setError('Please search for a location or Digipin first to see area bounds at different levels.');
      setTimeout(() => setError(''), 3000);
    }
      // If no location or Digipin, just update the level for future use
    else {
      console.log(`ℹ️ Area level updated to ${newLevel} - will be used for future searches`);
    }
    }, 300); // 300ms debounce delay
  };

     const clearAll = () => {
     // Clear all search/selection state
     setSearchQuery('');
     setSelectedLocation(null);
     setDigipin('');
     setError('');
     setShowToast(false); // Clear any existing toast messages
     setAreaBounds(null);
     setAreaCenter(null);
     setLoading(false);
     
     // Clear URL parameters
     const url = new URL(window.location);
     url.searchParams.delete('digipin');
     window.history.replaceState({}, '', url);
     
     // Remove existing UI elements
     if (markerRef.current && map) {
       map.removeLayer(markerRef.current);
       markerRef.current = null;
     }
     
     if (areaRectangle && map) {
       map.removeLayer(areaRectangle);
       setAreaRectangle(null);
     }
     
     // Reset location detection state to allow fresh detection
     setLocationDetectionComplete(false);
     setUserLocationReady(false);
     
     // Use the same function as page load to restore default view
      console.log('🔄 Clear button: Triggering requestLocationPermission for default view');
      requestLocationPermission();
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Full-screen map container */}
      <div 
        ref={mapRef}
        style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1
        }}
      />

             

               {/* Search panel - draggable */}
        <div 
          style={{
          position: 'absolute',
            top: `${searchPanelPosition.y}px`,
            left: isCollapsed ? '50%' : `${searchPanelPosition.x}px`,
            transform: isCollapsed ? 'translateX(-50%)' : 'none',
          zIndex: 1000,
            width: isCollapsed ? 'min(520px, 95vw)' : '300px',
            maxHeight: isCollapsed ? 'auto' : 'calc(100vh - 20px)',
            overflowY: isCollapsed ? 'visible' : 'auto',
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            border: '1px solid #eee',
            userSelect: 'auto'
          }}
          className={`search-panel ${isCollapsed ? 'collapsed' : ''}`}
        >
        {/* Header with controls */}
        <div 
          style={{
            // padding: '5px',
            borderBottom: isCollapsed ? 'none' : '1px solid #eee',
            background: '#f8f9fa',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: (!isCollapsed && panelMode === 'search') ? 'center' : 'space-between',
            gap: '10px',
            cursor: isCollapsed ? (isDragging ? 'grabbing' : 'grab') : 'default'
          }}
          onMouseDown={isCollapsed ? handleDragMouseDown : undefined}
        >
          {/* Mobile DigiPin display when collapsed, or search input */}
          {isCollapsed ? (
            <>
              {panelMode === 'search' ? (
                // Search input mode
                <>
                  <div style={{ width: '100%' }}>
                    <input
                      type="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search location..."
                      autoFocus
                      enterKeyHint="search"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleMobileSearch(e);
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px',
                        outline: 'none',
                        marginBottom: '8px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        type="button"
                        onClick={handleMobileSearch}
                        disabled={!searchQuery.trim()}
                        style={{
                          flex: 1,
                          padding: '8px 16px',
                          background: searchQuery.trim() ? '#007bff' : '#0057D2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '14px',
                          cursor: searchQuery.trim() ? 'pointer' : 'not-allowed',
                          transition: 'all 0.2s ease'
                        }}
                        title="Search location"
                      >
                        Search
                      </button>
                      <button
                        type="button"
                        onClick={handleSearchToggle}
                        style={{
                          flex: 1,
                          padding: '8px 16px',
                          background: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        title="Cancel search"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                // DigiPin display mode - two row layout
                <div style={{ width: '100%' }}>
                  {/* First Row: Logo, DigiPin Text, and Expand/Collapse button */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    margin: '8px'
                  }}>
                    
                    {/* Left: Pin2Point Logo */}
                    <a 
                      href="https://www.pin2point.com" 
                      // target="_blank" 
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none' }}
                    >
                      <img 
                        src={useSmallLogo ? "/PNG/Pin2Point Blue Logo.png" : "/PNG/Pin2Point_Blue_font.png"} 
                        alt="Pin2Point" 
                        style={{
                          height: '20px',
                          width: 'auto',
                          flexShrink: 0
                        }}
                      />
                    </a>
                    
                    {/* Center: DigiPin Text with prefix */}
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      fontFamily: '"ChampagneLimousines", Sans-serif',
                      fontWeight: 'bold',
                      color: '#0057D2',
                      marginLeft: '8px',
                      marginRight: '8px',
                      overflow: 'hidden'
                    }}>
                      <span style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        textAlign: 'center'
                      }}>
                        Digipin: {digipin || 'No DigiPin'}
                      </span>
                    </div>
                    
                    {/* Right: Action Icons and Expand/Collapse button */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px',
                      flexShrink: 0
                    }}>
                      {/* Search button */}
                      <button
                        onClick={handleSearchToggle}
                        className="mobile-action-button"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          borderRadius: '3px',
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '20px',
                          height: '20px'
                        }}
                        title="Search location"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="11" cy="11" r="8" stroke="#6D28D9" strokeWidth="2"/>
                          <path d="m21 21-4.35-4.35" stroke="#6D28D9" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                      
                      {/* Expand/Collapse button */}
                      <button
                        onClick={handleExpandCollapse}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '14px',
                          color: '#6c757d',
                          padding: '4px',
                          borderRadius: '3px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '20px',
                          height: '20px',
                          transition: 'all 0.2s ease',
                          flexShrink: 0
                        }}
                        className="expand-collapse-btn"
                        title={isCollapsed ? 'Expand panel' : 'Collapse panel'}
                      >
                        <svg 
                          width="10" 
                          height="10" 
                          viewBox="0 0 12 12" 
                          fill="none" 
                          style={{
                            transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                            transition: 'transform 0.3s ease'
                          }}
                        >
                          <path 
                            d="M3 4.5L6 7.5L9 4.5" 
                            stroke="currentColor" 
                            strokeWidth="1.5" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                </div>
              )}
              
            </>
          ) : (
            // Expanded mode - show title and expand/collapse button
            <>
              {/* Pin2Point Logo */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px 15px',
                background: '#f8f9fa',
                borderBottomLeftRadius: '8px',
                borderBottomRightRadius: '8px',
                width: panelMode === 'search' ? '100%' : 'auto'
              }}>
                <a 
                  href="https://www.pin2point.com" 
                  // target="_blank" 
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <img 
                    src="/PNG/Pin2Point_Blue_font.png" 
                    alt="Pin2Point" 
                    style={{
                      height: '20px',
                      width: 'auto',
                      flexShrink: 0
                    }}
                  />
                </a>
              </div>
            </>
          )}


          {/* Expand/Collapse button - hidden when collapsed or in search mode */}
          {!isCollapsed && panelMode !== 'search' && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: '8px 15px',
              background: '#f8f9fa',
              borderBottomLeftRadius: '8px',
              borderBottomRightRadius: '8px'
            }}>
              {/* Expand/Collapse button */}
              <button
                onClick={handleExpandCollapse}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#6c757d',
                  padding: '6px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '24px',
                  height: '24px',
                  transition: 'all 0.2s ease'
                }}
                className="expand-collapse-btn"
                title={isCollapsed ? 'Expand panel' : 'Collapse panel'}
              >
                <svg 
                  width="12" 
                  height="12" 
                  viewBox="0 0 12 12" 
                  fill="none" 
                  style={{
                    transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                    transition: 'transform 0.3s ease'
                  }}
                >
                  <path 
                    d="M3 4.5L6 7.5L9 4.5" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>


        {/* Collapsible content */}
        {!isCollapsed && (
          <>
        {/* Search Section */}
        <div style={{
          padding: '15px',
          borderBottom: '1px solid #eee'
        }}>
          <form onSubmit={handleSearch}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold', fontSize: '13px' }}>
                Search Location:
              </label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Enter pincode, city or DigiPin to search"
                required
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
          <button
            type="submit"
            disabled={loading}
                style={{
                  flex: 1,
                  padding: '10px',
                  background: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? 'Searching...' : 'Search Location'}
              </button>
              <button
                type="button"
                onClick={clearAll}
                style={{
                  padding: '10px 12px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
                title="Clear all"
              >
                Clear
          </button>
            </div>
        </form>
           {/* Digipin Section */}
          <div style={{ marginBottom: '12px' }}>
            <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 'bold', color: '#333' }}>Digipin:</h4>
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              {/* Digipin */}
              {digipin && (
                <div style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  flex: '1',
                  minWidth: '200px'
                }}>
                  <div style={{ 
                    flex: 1,
                    fontSize: '14px', 
                    fontFamily: '"ChampagneLimousines", Sans-serif', 
                    fontWeight: 'bold', 
                    color: '#0057D2',
                    padding: '8px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb'
                  }}>
                    {digipin}
                  </div>
                  <button
                    onClick={(e) => {
                      navigator.clipboard.writeText(digipin);
                      
                      // Get the button element (handle clicks on SVG elements)
                      const button = e.target.closest('button') || e.target;
                      
                      // Show success state briefly
                      button.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>';
                      button.style.color = '#10b981';
                      
                      // Reset after 1.5 seconds
                      setTimeout(() => {
                        button.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="#6D28D9" strokeWidth="2" fill="none"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="#6D28D9" strokeWidth="2" fill="none"/></svg>';
                        button.style.color = '#6D28D9';
                      }, 1500);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#6D28D9',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '4px',
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '24px',
                      height: '24px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#f3f4f6';
                      e.target.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'none';
                      e.target.style.transform = 'scale(1)';
                    }}
                    title="Copy DigiPin to clipboard"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="#6D28D9" strokeWidth="2" fill="none"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="#6D28D9" strokeWidth="2" fill="none"/>
                    </svg>
                  </button>
                </div>
              )}                          
            </div>
          </div>

           {/* Area Level Control */}
           <div style={{ marginBottom: '12px' }}>
           <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 'bold', color: '#333' }}>
            Digipin Level:
             </h4>
             
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="range"
                min="1"
                max="10"
                value={areaLevel}
                onChange={(e) => {
                  const newLevel = parseInt(e.target.value);
                  console.log(`🎚️ Range input changed to: ${newLevel}`);
                  handleAreaLevelChange(newLevel);
                }}
                style={{
                  flex: 1,
                  height: '6px',
                  borderRadius: '3px',
                  background: '#ddd',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: '12px', color: '#666', minWidth: '30px' }}>
                {areaLevel}
              </span>
            </div>
            <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
              {areaLevel === 1 ? '~1000km × 1000km' :
               areaLevel === 2 ? '~250km × 250km' :
               areaLevel === 3 ? '~62.5km × 62.5km' :
               areaLevel === 4 ? '~15.6km × 15.6km' :
               areaLevel === 5 ? '~3.9km × 3.9km' :
               areaLevel === 6 ? '~1km × 1km' :
               areaLevel === 7 ? '~250m × 250m' :
               areaLevel === 8 ? '~60m × 60m' :
               areaLevel === 9 ? '~15m × 15m' :
               '~3.8m × 3.8m'}
            </div>
          </div>
      </div>

        {/* Location Information Section */}
        <div style={{
          padding: '15px'
        }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 'bold', color: '#333' }}>
            Address
          </h4>        

          {selectedLocation && (
            <div style={{ marginBottom: '12px' }}>              
              <div style={{ fontSize: '12px', fontWeight: '500', color: '#333' }}>
                {selectedLocation.address}
              </div>
            </div>
          )}          

          {/* Actions Menu - 3x3 Grid */}
          <div style={{ marginBottom: '12px' }}>
            {/* <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 'bold', color: '#333' }}>Actions:</h4> */}
            <div className="mobile-action-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '8px'
            }}>
              {/* Google Maps button */}
              {selectedLocation && selectedLocation.lat && selectedLocation.lng && (
                <button
                  onClick={() => {
                    const url = `https://maps.google.com/maps?q=${selectedLocation.lat},${selectedLocation.lng}&z=15`;
                    window.open(url, '_blank');
                  }}
                  className="mobile-action-menu-button"
                  style={{
                    background: 'none',
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    padding: '12px 8px',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    minHeight: '60px'
                  }}
                  title="Open in Google Maps"
                >
                  <span className="material-icons" style={{ fontSize: '20px', color: '#4285F4' }}>
                    map
                  </span>
                  <span style={{ fontSize: '11px', fontWeight: '500', color: '#374151' }}>View in Google Maps</span>
                </button>
              )}
              
              {/* Copy button */}
              <button
                onClick={() => {
                  copyDigipin();
                }}
                disabled={!digipin || digipin === 'N/A'}
                className="mobile-action-menu-button"
                style={{
                  background: 'none',
                  border: '1px solid #e5e7eb',
                  cursor: digipin && digipin !== 'N/A' ? 'pointer' : 'not-allowed',
                  padding: '12px 8px',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  minHeight: '60px',
                  opacity: digipin && digipin !== 'N/A' ? 1 : 0.5
                }}
                title="Copy DigiPin"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke={digipin && digipin !== 'N/A' ? "#6D28D9" : "#ccc"} strokeWidth="2" fill="none"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke={digipin && digipin !== 'N/A' ? "#6D28D9" : "#ccc"} strokeWidth="2" fill="none"/>
                </svg>
                <span style={{ fontSize: '11px', fontWeight: '500', color: '#374151' }}>Copy</span>
              </button>
              
              {/* Directions button */}
              {selectedLocation && selectedLocation.lat && selectedLocation.lng && currentLocation && 
               Math.abs(currentLocation.lat - selectedLocation.lat) > 0.001 && 
               Math.abs(currentLocation.lng - selectedLocation.lng) > 0.001 && (
                <button
                  onClick={() => {
                    const url = `https://maps.google.com/maps/dir/${currentLocation.lat},${currentLocation.lng}/${selectedLocation.lat},${selectedLocation.lng}`;
                    window.open(url, '_blank');
                  }}
                  className="mobile-action-menu-button"
                  style={{
                    background: 'none',
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    padding: '12px 8px',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    minHeight: '60px'
                  }}
                  title="Get directions"
                >
                  <span className="material-icons" style={{ fontSize: '20px', color: '#34A853' }}>
                    directions
                  </span>
                  <span style={{ fontSize: '11px', fontWeight: '500', color: '#374151' }}>Directions</span>
                </button>
              )}
              
              {/* QR Code button */}
              {digipin && digipin !== 'N/A' && (
                <button
                  onClick={() => {
                    generateQRCode();
                  }}
                  className="mobile-action-menu-button"
                  style={{
                    background: 'none',
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    padding: '12px 8px',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    minHeight: '60px'
                  }}
                  title="Generate QR Code"
                >
                  <span className="material-icons" style={{ fontSize: '20px', color: '#3B82F6' }}>
                    qr_code
                  </span>
                  <span style={{ fontSize: '11px', fontWeight: '500', color: '#374151' }}>QR Code</span>
                </button>
              )}
              
              {/* Share button */}
              {digipin && digipin !== 'N/A' && (
                <button
                  onClick={() => {
                    shareDigipin();
                  }}
                  className="mobile-action-menu-button"
                  style={{
                    background: 'none',
                    border: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    padding: '12px 8px',
                    borderRadius: '6px',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    minHeight: '60px'
                  }}
                  title="Share Digipin"
                >
                  <span className="material-icons" style={{ fontSize: '20px', color: '#10B981' }}>
                    share
                  </span>
                  <span style={{ fontSize: '11px', fontWeight: '500', color: '#374151' }}>Share</span>
                </button>
              )}
              
              {/* Map Layer Controls - Show only non-selected layer */}
              {mapLayers && Object.keys(mapLayers).length > 0 && (
                Object.keys(mapLayers)
                  .filter(name => name !== currentLayerName) // Only show non-selected layers
                  .map((name) => (
                    <button
                      key={name}
                      onClick={() => {
                        if (!map) return;
                        
                        console.log(`🗺️ Switching to ${name} layer`);
                        
                        // Remove current layers
                        if (currentLayer) {
                          if (typeof currentLayer === 'object' && currentLayer.type === 'single') {
                            // Handle single layer removal (both street and satellite are now single type)
                            if (currentLayer.base && map.hasLayer(currentLayer.base)) {
                              map.removeLayer(currentLayer.base);
                            }
                          } else if (map.hasLayer(currentLayer)) {
                            map.removeLayer(currentLayer);
                          }
                        }
                        
                        // Add new layer
                        const newLayer = mapLayers[name];
                        if (newLayer.type === 'single') {
                          // Handle single layer addition (both street and satellite)
                          if (newLayer.base) {
                            newLayer.base.addTo(map);
                          }
                        } else {
                          // Fallback for any other layer types
                          if (newLayer.base) {
                            newLayer.base.addTo(map);
                          }
                        }
                        
                        setCurrentLayer(newLayer);
                        setCurrentLayerName(name);
                      }}
                      className="mobile-action-menu-button"
                      style={{
                        background: 'none',
                        border: '1px solid #e5e7eb',
                        cursor: 'pointer',
                        padding: '12px 8px',
                        borderRadius: '6px',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        minHeight: '60px'
                      }}
                      title={`Switch to ${name}`}
                    >
                      <span style={{ fontSize: '20px' }}>
                        {name === 'Street Map' && '🗺️'}
                        {name === 'Satellite' && '🛰️'}
                      </span>
                      <span style={{ fontSize: '11px', fontWeight: '500', color: '#374151' }}>
                        {name}
                      </span>
                    </button>
                  ))
              )}
            </div>
          </div>

          {areaLoading && (
            <div style={{ textAlign: 'center', padding: '12px' }}>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid #f3f4f6',
                borderTop: '2px solid #dc2626',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 8px'
              }}></div>
              <div style={{ fontSize: '11px', color: '#666' }}>Calculating area bounds...</div>
            </div>
          )}

          {error && (
            <div style={{ 
              padding: '8px', 
              backgroundColor: '#fef2f2', 
              border: '1px solid #fecaca',
              borderRadius: '4px',
              color: '#dc2626',
              fontSize: '12px',
              marginTop: '12px'
            }}>
              {error}
            </div>
          )}
        </div>
          </>
        )}
      </div>



      {/* Loading Overlay */}
      {loading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            textAlign: 'center',
            padding: '20px',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid #dee2e6'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #007bff',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 10px'
            }}></div>
            <p style={{ margin: 0, color: '#333', fontSize: '16px', fontWeight: '500' }}>
              Searching for location...
            </p>
            <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
              Please wait while we search for your location and get details
            </p>
      </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && qrCodeDataUrl && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#1f2937', fontSize: '18px' }}>
              QR Code Generated
            </h3>
            
            <div style={{ marginBottom: '20px' }}>
              <img 
                src={qrCodeDataUrl} 
                alt="QR Code" 
                style={{ 
                  width: '200px', 
                  height: '200px', 
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }} 
              />
            </div>
            
            <div style={{ marginBottom: '20px', fontSize: '14px', color: '#6b7280' }}>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Digipin:</strong> {digipin}
              </p>
              <p style={{ margin: '0 0 8px 0' }}>
                <strong>Location:</strong> {selectedLocation?.address || 'Unknown'}
              </p>
              <p style={{ margin: '0', fontSize: '12px' }}>
                Scan this QR code to quickly access this location
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = qrCodeDataUrl;
                  link.download = `digipin-${digipin}-qr.png`;
                  link.click();
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#10B981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#059669';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#10B981';
                }}
              >
                Download QR Code
              </button>
              
              <button
                onClick={() => shareQRCodeFromModal()}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#2563EB';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#3B82F6';
                }}
              >
                Share QR Code
              </button>
              
              <button
                onClick={() => setShowQRModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#4B5563';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#6B7280';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Location Permission Prompt Modal */}
      {showLocationPrompt && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              backgroundColor: '#3B82F6',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '24px'
            }}>
              📍
            </div>
            
            <h3 style={{ margin: '0 0 16px 0', color: '#1f2937', fontSize: '18px', fontWeight: '600' }}>
              Location Access Required
            </h3>
            
            <p style={{ 
              margin: '0 0 24px 0', 
              color: '#6b7280', 
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              To view the Digipin of your location, please allow location sharing.
            </p>
            
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={handleLocationPermissionGranted}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#2563EB';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#3B82F6';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                <span className="material-icons" style={{ fontSize: '18px' }}>
                  my_location
                </span>
                Allow Location
              </button>
              
              <button
                onClick={handleLocationPermissionDenied}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6B7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#4B5563';
                  e.target.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#6B7280';
                  e.target.style.transform = 'translateY(0)';
                }}
              >
                Don't Allow
              </button>
            </div>
            
            
            <p style={{ 
              margin: '16px 0 0 0', 
              color: '#9ca3af', 
              fontSize: '12px',
              lineHeight: '1.4'
            }}>
              Sharing you location will allow us to show you the Digipin of your exact location. 
              If you prefer not to share your exact location, we will show approximate location which might not be accurate. 
              You can always click anywhere on the map to find the Digipin of that location.
            </p>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: '#333',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '6px',
          fontSize: '14px',
          zIndex: 2000,
          animation: 'slideInUp 0.3s ease-out',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          maxWidth: '90vw',
          minWidth: '200px'
        }}>
          <span style={{ flex: 1 }}>{toastMessage}</span>
          <button
            onClick={dismissToast}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '3px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              lineHeight: 1,
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.2)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            title="Dismiss"
          >
            ×
          </button>
        </div>
      )}


      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes slideInUp {
            from {
              transform: translateY(100%);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          @keyframes slideDown {
            from {
              transform: translateY(-10px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          }
          
          /* Prevent iOS Safari zoom on input focus - only on mobile */
          @media (max-width: 768px) {
            input[type="text"], input[type="search"] {
              font-size: 16px !important;
              -webkit-appearance: none;
              border-radius: 0;
            }
          }
          
          /* Additional iOS-specific fix */
          @supports (-webkit-touch-callout: none) {
            @media (max-width: 768px) {
              input[type="text"], input[type="search"] {
                font-size: 16px !important;
              }
            }
          }
          
          /* Ensure collapsed panel stays within viewport */
          @media (max-width: 768px) {
            .search-panel.collapsed {
              max-width: 95vw !important;
              left: 50% !important;
              transform: translateX(-50%) !important;
            }
            
            /* Mobile action menu improvements */
            .mobile-action-button {
              min-height: 28px !important;
              min-width: 28px !important;
              touch-action: manipulation;
              -webkit-tap-highlight-color: transparent;
            }
            
            .mobile-action-button:active {
              transform: scale(0.95);
              transition: transform 0.1s ease;
            }
            
            /* Improve touch targets for mobile */
            button {
              min-height: 44px;
              touch-action: manipulation;
            }
            
            /* Mobile action menu grid adjustments */
            .mobile-action-grid {
              grid-template-columns: repeat(3, 1fr) !important;
              gap: 12px !important;
            }
            
            /* Mobile action menu button styling */
            .mobile-action-menu-button {
              padding: 16px 12px !important;
              min-height: 70px !important;
              border-radius: 8px !important;
              font-size: 12px !important;
            }
            
            .mobile-action-menu-button:active {
              background-color: #f3f4f6 !important;
              transform: scale(0.98);
            }
          }
        `}
      </style>
    </div>
  );
};

export default DigipinFinder;
